import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Menu as MenuIcon } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PlatformNav, SidebarFoot, PNAV, groupOf, firstOf } from "@/components/platform/PlatformNav";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VendorsAdmin } from "@/components/platform/VendorsAdmin";
import { Payouts } from "@/components/platform/DirectoryAdmin";
import { Overview } from "@/components/platform/Overview";
import { CustomerOrders } from "@/components/platform/orders/CustomerOrders";
import { Payments } from "@/components/platform/orders/Payments";
import { EditorHome } from "@/components/platform/editor/EditorHome";
import { EditorSlider } from "@/components/platform/editor/EditorSlider";
import { EditorPages } from "@/components/platform/editor/EditorPages";
import { EditorPricing } from "@/components/platform/editor/EditorPricing";
import { AdsAdmin } from "@/components/platform/AdsAdmin";
import { SharesAdmin } from "@/components/platform/SharesAdmin";
import { ReviewsAdmin } from "@/components/platform/ReviewsAdmin";
import { UsersAdmin } from "@/components/platform/UsersAdmin";
import { SettingsAdmin } from "@/components/platform/settings/SettingsAdmin";
import { PlatformProfile } from "@/components/platform/settings/PlatformProfile";

const call = async (fn, msg, done) => { try { await fn(); toast.success(msg); done(); } catch (e) { toast.error(formatErr(e)); } };

const Admins = () => {
  const [a, setA] = useState(null);
  const [email, setEmail] = useState("");
  const load = () => api.get("/platform/admin/admins").then((r) => setA(r.data));
  useEffect(() => { load(); }, []);
  if (!a) return null;
  return (
    <div className="card-surface space-y-2 p-4" data-testid="platform-admins">
      <p className="text-sm font-semibold text-white">Platform admins</p>
      {a.owners.map((o) => <p key={o} className="text-xs text-slate-300">{o} <span className="text-emerald-400">Owner</span></p>)}
      {a.admins.map((x) => <p key={x.email} className="flex items-center gap-2 text-xs text-slate-300">{x.email}{a.me_owner && <button type="button" className="text-rose-400" onClick={() => call(() => api.delete(`/platform/admin/admins/${x.email}`), "Removed", load)} data-testid={`admin-remove-${x.email}`}>Remove</button>}</p>)}
      {a.me_owner && <><div className="flex gap-2"><Input value={email} placeholder="email@example.com" className="h-8 text-xs" onChange={(e) => setEmail(e.target.value)} data-testid="platform-admin-email-input" /><Button size="sm" onClick={() => call(() => api.post("/platform/admin/admins", { email }), "Admin added", () => { setEmail(""); load(); })} data-testid="platform-admin-add-button">Add</Button></div>
        <p className="text-[11px] text-slate-500">New admins get an email link to set their password.</p></>}
    </div>
  );
};

const Releases = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ version: "", title: "", body: "" });
  const load = () => api.get("/platform/admin/releases").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="card-surface space-y-2 p-4" data-testid="platform-releases">
      <p className="text-sm font-semibold text-white">Master app versions</p>
      <p className="text-xs text-slate-500">The newest version here is the master. Vendors on an older version are flagged in the Vendors tab.</p>
      <div className="flex gap-2"><Input value={f.version} placeholder="1.1.0" className="w-28" onChange={(e) => setF({ ...f, version: e.target.value })} data-testid="release-version-input" /><Input value={f.title} placeholder="Title" onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="release-title-input" /></div>
      <Textarea rows={2} value={f.body} placeholder="What changed" onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="release-body-input" />
      <Button size="sm" disabled={!/^\d+\.\d+\.\d+$/.test(f.version) || f.title.length < 2 || f.body.length < 2} onClick={() => call(() => api.post("/platform/admin/releases", f), "Version added", () => { setF({ version: "", title: "", body: "" }); load(); })} data-testid="release-publish-button">Add version</Button>
      {rows.map((r) => <div key={r.id} className="border-t border-slate-800 pt-2 text-xs"><p className="font-semibold text-white">v{r.version} · {r.title} <span className="font-normal text-slate-500">{fmtDateTime(r.created_at)}</span> <button type="button" className="text-rose-400" onClick={() => call(() => api.delete(`/platform/admin/releases/${r.id}`), "Deleted", load)} data-testid={`release-delete-${r.id}`}>Delete</button></p><p className="whitespace-pre-line text-slate-400">{r.body}</p></div>)}
    </div>
  );
};

function Section({ s, go }) {
  const [g, sub] = s.split("/");
  if (g === "vendors") return sub === "payouts" ? <Payouts /> : sub === "reviews" ? <ReviewsAdmin /> : <VendorsAdmin />;
  if (g === "overview") return <Overview go={go} />;
  if (g === "orders") return sub === "vendors" ? <Payments who="vendor" /> : sub === "payments" ? <Payments /> : <CustomerOrders />;
  if (g === "editor") return sub === "pricing" ? <EditorPricing /> : sub === "pages" ? <EditorPages /> : sub === "slider" ? <EditorSlider /> : <EditorHome />;
  if (g === "ads" && sub === "shares") return <SharesAdmin />;
  if (g === "ads") return <AdsAdmin view={sub || "prices"} />;
  if (g === "users") return sub === "profile" ? <PlatformProfile /> : <UsersAdmin />;
  if (g === "team") return <div className="max-w-2xl">{sub === "versions" ? <Releases /> : <Admins />}</div>;
  if (g === "settings") return <SettingsAdmin view={sub || "site"} />;
  return <Overview go={go} />;
}

const VALID = new Set(PNAV.flatMap((x) => (x.children ? x.children.map(([to]) => to) : [x.id])));
const LABEL = Object.fromEntries(PNAV.flatMap((x) => (x.children ? x.children.map(([to, l]) => [to, `${x.label} · ${l}`]) : [[x.id, x.label]])));

export default function PlatformAdmin() {
  const [ok, setOk] = useState(null);
  const [sp, setSp] = useSearchParams();
  const OLD = { payouts: "vendors/payouts", reviews: "vendors/reviews", profile: "users/profile" };
  const raw = OLD[sp.get("s")] || sp.get("s") || "overview";
  const section = VALID.has(raw) ? raw : VALID.has(firstOf(raw)) ? firstOf(raw) : "overview";
  const [openId, setOpenId] = useState(groupOf(section));
  const [sheet, setSheet] = useState(false);
  const { user, logout } = useAuth();
  useEffect(() => { api.get("/platform/admin/admins").then(() => setOk(true)).catch((e) => setOk(formatErr(e))); }, []);
  useEffect(() => { setOpenId(groupOf(section)); }, [section]);
  if (ok === null) return null;
  if (ok !== true) return <div className="p-10 text-center text-slate-300" data-testid="platform-admin-error">{ok}. <Link to="/signin?next=/platform" className="text-emerald-400">Sign in as a platform admin</Link></div>;
  const go = (to) => { setSp({ s: to }); setSheet(false); window.scrollTo(0, 0); };
  const out = () => logout().then(() => { window.location.href = "/signin"; });
  const nav = (isSheet) => <PlatformNav section={section} openId={openId} setOpenId={setOpenId} go={go} sheet={isSheet} />;
  return (
    <div className="min-h-screen bg-[#060A12] lg:flex" data-testid="platform-admin">
      <aside className="hidden border-r border-slate-800 bg-[#070C17] lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:shrink-0 lg:flex-col" data-testid="platform-sidebar">
        <div className="p-5"><p className="text-[10px] font-bold uppercase tracking-[0.3em] text-emerald-400">All Sports Venues</p><p className="font-display text-xl font-black uppercase text-white">Platform admin</p></div>
        <div className="flex-1 overflow-y-auto pb-4">{nav(false)}</div>
        <SidebarFoot email={user?.email} onLogout={out} />
      </aside>
      <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-800 bg-[#070C17]/95 px-4 py-3 backdrop-blur lg:hidden" data-testid="platform-topbar">
        <button type="button" onClick={() => setSheet(true)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-700 text-slate-200" aria-label="Open menu" data-testid="platform-menu-button"><MenuIcon className="h-4 w-4" /></button>
        <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-[0.3em] text-emerald-400">Platform admin</p><p className="truncate text-sm font-semibold text-white" data-testid="platform-current-section">{LABEL[section]}</p></div>
      </div>
      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent side="left" className="w-72 overflow-y-auto border-slate-800 bg-[#070C17] p-0" data-testid="platform-menu-sheet">
          <SheetHeader className="p-5 pb-2"><SheetTitle className="text-left text-white">Menu</SheetTitle></SheetHeader>
          {nav(true)}
          <SidebarFoot email={user?.email} onLogout={out} sheet />
        </SheetContent>
      </Sheet>
      <main className="min-w-0 flex-1 p-5 sm:p-8" data-testid={`platform-section-${section.replace("/", "-")}`}><Section s={section} go={go} /></main>
    </div>
  );
}
